import React from 'react';

interface ModalProps {
    title?: string;
    onClose?: () => void;
    headerVariant?: 'default' | 'outcome' | 'opportunity' | 'solution' | 'story' | 'experiment' | 'enablerNode';
    children?: React.ReactNode;
    /** When true, clicking the overlay will call onClose. */
    closeOnOverlayClick?: boolean;
    /** Additional class applied to the inner panel (keeps existing .modal-panel class). */
    className?: string;
    /** When true, don't render the default header; useful for custom headers. */
    hideHeader?: boolean;
    /** Additional props to spread onto the overlay element (role/aria attributes, extra classes). */
    overlayProps?: React.HTMLAttributes<HTMLDivElement>;
    /** Optional id to apply to the header label element for aria-labelledby. */
    headerId?: string;
    /** Disable the header close button (keeps it visible but disabled). */
    closeDisabled?: boolean;
    /** Optional extra class applied to the close button. */
    closeButtonClassName?: string;
    /** Additional props to spread onto the close button. */
    closeButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
}

export default function Modal({
    title,
    onClose,
    headerVariant = 'default',
    children,
    closeOnOverlayClick = false,
    className = '',
    hideHeader = false,
    overlayProps,
    headerId,
    closeDisabled = false,
    closeButtonClassName,
    closeButtonProps,
}: ModalProps) {
    const headerClass = `modal-header modal-header--${headerVariant}`;
    const { className: overlayClassName, ...restOverlay } = overlayProps || {};
    const overlayClass = ['modal-overlay', overlayClassName].filter(Boolean).join(' ');

    const handleOverlayClick = () => {
        if (closeOnOverlayClick && onClose) onClose();
    };

    return (
        <div {...restOverlay} className={overlayClass} onClick={handleOverlayClick}>
            <div className={`modal-panel ${className}`} onClick={(e) => e.stopPropagation()}>
                {!hideHeader && (
                    <div className={headerClass}>
                        <span className='modal-type-label' {...(headerId ? { id: headerId } : {})}>
                            {title}
                        </span>
                        {onClose && (
                            <button
                                onClick={onClose}
                                className={['modal-btn', 'modal-btn-close', closeButtonClassName].filter(Boolean).join(' ')}
                                disabled={closeDisabled}
                                // The glyph alone gives screen readers no usable
                                // name; callers can override via closeButtonProps.
                                aria-label='Close'
                                {...closeButtonProps}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                )}
                <div className='modal-body'>{children}</div>
            </div>
        </div>
    );
}
